import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import "../../Css/Activity/ActivityFeedView.css";

export function AccountSearchDialog({
  title,
  children,
  onClose,
  anchor,
  returnFocus,
  compact = false,
  className = "",
  footer,
  filter = false,
  subtitle,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  anchor?: HTMLElement | null;
  returnFocus?: HTMLElement | null;
  compact?: boolean;
  className?: string;
  footer?: ReactNode;
  filter?: boolean;
  subtitle?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const previous =
      returnFocus ?? anchor ?? (document.activeElement as HTMLElement | null);
    const dialog = ref.current;
    dialog?.showModal();
    return () => {
      dialog?.close();
      previous?.focus();
    };
  }, [anchor, returnFocus]);
  useLayoutEffect(() => {
    if (!anchor) return;
    const position = () => {
      const dialog = ref.current;
      if (!dialog) return;
      const rect = anchor.getBoundingClientRect();
      const width = dialog.getBoundingClientRect().width;
      dialog.style.left =
        Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)) +
        "px";
      dialog.style.top =
        Math.max(
          12,
          Math.min(
            rect.bottom + 8,
            window.innerHeight - dialog.offsetHeight - 12,
          ),
        ) + "px";
    };
    const frame = requestAnimationFrame(position);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(position);
    if (ref.current) observer?.observe(ref.current);
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
    };
  }, [anchor]);
  return createPortal(
    <dialog
      ref={ref}
      className={filter ? `activityFilter_popover tableFilterPopover ${className}` : `accountSearch_dialog${anchor ? " accountSearch_popover" : ""}${compact ? " accountSearch_popover--compact" : ""} ${className}`}
      aria-label={title}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        const rect = event.currentTarget.getBoundingClientRect();
        if (
          event.clientX < rect.left ||
          event.clientX > rect.right ||
          event.clientY < rect.top ||
          event.clientY > rect.bottom
        )
          onClose();
      }}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div className={filter ? "activityFilter_popover_header" : "accountSearch_dialogHeading"}>
        {filter ? <div className="activityFilter_popover_headerText">
          <h2 className="activityFilter_popover_title">{title}</h2>
          {subtitle && <p className="activityFilter_popover_subtitle">{subtitle}</p>}
        </div> : <h2>{title}</h2>}
        <button
          type="button"
          className={filter ? "activityFilter_popover_close" : "accountSearch_close"}
          onClick={onClose}
          aria-label={`Close ${title}`}
        >
          <span className="fa-solid fa-xmark" aria-hidden />
        </button>
      </div>
      {footer ? <><div className="accountSearch_dialogBody">{children}</div><footer className="accountSearch_dialogFooter">{footer}</footer></> : children}
    </dialog>,
    document.body,
  );
}
